import {toast} from '@wandb/weave/common/components/elements/Toast';
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import copyToClipboard from 'copy-to-clipboard';
import React, {useState} from 'react';

import DocUrls from '../../../../util/doc_urls';

const LOG_ARTIFACT_CODE_SNIPPET = (entityName?: string) => `
# Initialize a W&B run
run = wandb.init(project="my-project", entity="${entityName ?? 'my-entity'}")

# Create an artifact and log it
artifact = wandb.Artifact(
  name="my-artifact",
  type="my-artifact-type"
)
artifact.add_dir("path/to/your/files")
run.log_artifact(artifact)
run.finish()
`;

enum FAQ_Section {
  Section1,
  Section2,
  Section3,
  Section2_Code,
}

export const FAQs = (props: {entityName?: string}) => {
  const {entityName} = props;
  const [expandedSections, setExpandedSections] = useState<FAQ_Section[]>([]);
  const onToggleSectionExpanded = (section: FAQ_Section) => {
    if (expandedSections.includes(section)) {
      const indexToRemove = expandedSections.indexOf(section);
      setExpandedSections(
        expandedSections.filter((_, i) => i !== indexToRemove)
      );
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };
  const isSectionExpanded = (section: FAQ_Section) => {
    return expandedSections.includes(section);
  };

  const onCopyLogArtifactClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(LOG_ARTIFACT_CODE_SNIPPET(entityName));
    toast('Copied to clipboard');
  };
  return (
    <Tailwind style={{width: '100%', borderRadius: '4px'}}>
      <div className="mt-24 flex flex-col justify-center rounded p-1">
        <span className="flex items-center gap-8 rounded bg-white px-16 py-12 text-xl leading-8">
          <Icon name="help-alt" />
          FAQs
        </span>
        <div
          className={clsx(
            `${
              isSectionExpanded(FAQ_Section.Section1) ? 'rounded-t' : 'rounded'
            }`,
            'min-h-44 cursor-pointer bg-white p-10 hover:bg-moon-50'
          )}
          onClick={() => onToggleSectionExpanded(FAQ_Section.Section1)}>
          <div className="flex items-center text-base/8 font-semibold">
            <Button
              variant="ghost"
              size="small"
              icon={
                isSectionExpanded(FAQ_Section.Section1)
                  ? 'chevron-down'
                  : 'chevron-next'
              }
              onClick={() => onToggleSectionExpanded(FAQ_Section.Section1)}
              className="mr-10"
            />
            What is an artifact type?
          </div>
        </div>
        {isSectionExpanded(FAQ_Section.Section1) && (
          <>
            <div className="rounded-b bg-white px-40 py-16 text-base font-normal leading-5 text-moon-650">
              An artifact type in the W&B platform refers to the category or
              classification of the assets you log, such as models, datasets,
              hyperparameters, or any other resources relevant to your machine
              learning experiments. Assigning an artifact type helps organize
              and categorize your assets for easier navigation and analysis
              within the platform.
            </div>
          </>
        )}
        <div
          className={clsx(
            `${
              isSectionExpanded(FAQ_Section.Section2) ? 'rounded-t' : 'rounded'
            }`,
            'min-h-44 cursor-pointer bg-white p-10 hover:bg-moon-50'
          )}
          onClick={() => onToggleSectionExpanded(FAQ_Section.Section2)}>
          <div className="flex items-center text-base/8 font-semibold">
            <Button
              variant="ghost"
              size="small"
              icon={
                isSectionExpanded(FAQ_Section.Section2)
                  ? 'chevron-down'
                  : 'chevron-next'
              }
              onClick={() => onToggleSectionExpanded(FAQ_Section.Section2)}
              className="mr-10"
            />
            How do I log an artifact to an experiment?
          </div>
        </div>
        {isSectionExpanded(FAQ_Section.Section2) && (
          <>
            <div className="rounded-b bg-white px-40 py-16 text-base font-normal leading-5 text-moon-650">
              The following code snippet will show you how to log your artifact
              to a W&B Run.
              <div className="mt-16 flex flex-col rounded border border-solid border-moon-250 bg-moon-50 px-8">
                <div className="font-['Source Sans Pro'] flex flex-row items-center py-10 text-sm font-semibold">
                  <Button
                    variant="ghost"
                    size="small"
                    icon={
                      isSectionExpanded(FAQ_Section.Section2_Code)
                        ? 'chevron-down'
                        : 'chevron-next'
                    }
                    onClick={() =>
                      onToggleSectionExpanded(FAQ_Section.Section2_Code)
                    }
                    className="mr-4"
                  />
                  Code snippet
                  <div className="ml-auto">
                    <Button
                      icon="copy"
                      variant="ghost"
                      size="small"
                      onClick={onCopyLogArtifactClick}
                      tooltip="Copy to clipboard">
                      Copy
                    </Button>
                  </div>
                </div>
                {isSectionExpanded(FAQ_Section.Section2_Code) && (
                  <div className="flex-auto whitespace-pre-wrap px-8 pb-8 font-['Source_Code_Pro'] text-xs leading-4">
                    {LOG_ARTIFACT_CODE_SNIPPET(entityName)}
                  </div>
                )}
              </div>
              <div className="mb-12 mt-16 text-base font-normal leading-5 text-moon-650">
                <TargetBlank href={DocUrls.artifacts}>
                  <span className="cursor-pointer font-semibold text-teal-650 hover:text-teal-500">
                    Learn more
                  </span>
                </TargetBlank>{' '}
                about logging and versioning experiment outputs as W&B Artifacts
              </div>
            </div>
          </>
        )}
        <div
          className={clsx(
            `${
              isSectionExpanded(FAQ_Section.Section3) ? 'rounded-t' : 'rounded'
            }`,
            'min-h-44 cursor-pointer bg-white p-10 hover:bg-moon-50'
          )}
          onClick={() => onToggleSectionExpanded(FAQ_Section.Section3)}>
          <div className="flex items-center text-base/8 font-semibold">
            <Button
              variant="ghost"
              size="small"
              icon={
                isSectionExpanded(FAQ_Section.Section3)
                  ? 'chevron-down'
                  : 'chevron-next'
              }
              onClick={() => onToggleSectionExpanded(FAQ_Section.Section3)}
              style={{marginRight: '10px'}}
            />
            What are the key features of a Registry?
          </div>
        </div>
        {isSectionExpanded(FAQ_Section.Section3) && (
          <>
            <div className="rounded-b bg-white px-40 py-16 text-base font-normal leading-5 text-moon-650">
              Defining an artifact type for a registry allows you to specify the
              types of assets that can be logged and managed within that
              registry. This ensures clarity and consistency in organizing your
              assets based on their use cases or purposes.
              <div className="mt-16">
                There are default registries, such as the model registry, which
                come with a predefined asset type by default. While you cannot
                remove this default asset type, you do have the flexibility to
                add additional types. However, in custom registries, you have
                complete freedom to define the underlying asset types according
                to your specific needs and use cases.
              </div>
            </div>
          </>
        )}
      </div>
    </Tailwind>
  );
};
